import styled from 'styled-components';
import { GlobalToken } from 'antd';
import EPInput from '@/comp/EPInput';
import { getTableCss } from '@/comp/pages/EnginePage/Engine/Common/styled';

interface DefaultRuleContainerProps {
  $fromRulesEditor: boolean;
  $hasFinishedWizard: boolean;
  $isReadOnly?: boolean;
  theme: {
    token: GlobalToken;
  };
}

export const DefaultRuleContainer = styled.div<DefaultRuleContainerProps>`
  display: flex;
  flex-direction: column;
  
  ${({ theme: { token } }) => getTableCss(token)}

  .label-text {
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    color: ${({ theme: { token } }) => token.colorTextTertiary};
  }

  .no-value {
    font-size: ${({ theme: { token } }) => token.fontSize}px;
  }

  .modulate-checkbox {
    .ant-checkbox:not(.ant-checkbox-checked) .ant-checkbox-inner {
      background-color: ${({ theme: { token } }) => token.colorBgContainer};
      border-color: ${({ theme: { token } }) => token.colorPrimary};
    }
  }

  .view-mode-modulate-checkbox {
    &.ant-checkbox-wrapper-disabled {
      cursor: default;
    }

    &.ant-checkbox-wrapper-disabled:hover {
      cursor: default;
    }

    .ant-checkbox:not(.ant-checkbox-checked) .ant-checkbox-inner {
      background-color: ${({ theme: { token } }) => token.colorBgContainer};
      border-color: ${({ theme: { token } }) => token.colorBorder};
      border-width: ${({ theme: { token } }) => token.lineWidth}px;
    }

    .ant-checkbox.ant-checkbox-checked .ant-checkbox-inner {
      border: none;
      background-color: transparent;
    }

    .ant-checkbox.ant-checkbox-checked .ant-checkbox-inner::after {
      border-color: ${({ theme: { token } }) => token.colorBorder};
    }
  }

  .table {
    .ant-table {
      background-color: ${({ theme: { token } }) => token.colorBgContainer};
      .ant-table-thead > tr > th {
        background-color: ${({ theme: { token } }) => token.colorBgElevated};
        position: relative;
      }
    }
    ${({ $fromRulesEditor }: DefaultRuleContainerProps) =>
      $fromRulesEditor
        ? `
      .ant-table {
        background-color: ${({ theme: { token } }) => token.colorBgBase};

        .ant-table-cell-scrollbar {
          background-color: ${({ theme: { token } }) => token.colorBgElevated};
        }
      }

      .view-mode-table-cell {
        font-size: ${({ theme: { token } }) => token.fontSize}px;
                color: ${({ theme: { token } }) => token.colorBorder};
      }

      .margin-left-2 {
        margin-left: 2px;
      }

      .margin-left-4 {
        margin-left: ${({ theme: { token } }) => token.marginXXS}px;
      }

      .check-icon {
        width: 10px;
        color: ${({ theme: { token } }) => token.colorBorder};
      }
  
      .view-mode-modulate-checkbox {
        &.ant-checkbox-wrapper-disabled {
          cursor: default;
        }
  
        &.ant-checkbox-wrapper-disabled:hover {
          cursor: default;
        }
  
        .ant-checkbox.ant-checkbox-checked .ant-checkbox-inner {
          border: none;
          background-color: transparent;
        }
  
        .ant-checkbox.ant-checkbox-checked .ant-checkbox-inner::after {
          border-color: ${({ theme: { token } }) => token.colorBorder};
        }
      }
  `
        : `
        max-height: ${({ $hasFinishedWizard }) => ($hasFinishedWizard ? '509px' : '375px')}; 

        .ant-table-cell-scrollbar {
          background-color: ${({ theme: { token } }) => token.colorBgElevated};
        }

        .ant-table-tbody > tr > td.ant-table-cell:first-child {
          background-color: ${({ theme: { token } }) => token.colorBgElevated};
        }

        .disabled-compressor {
          opacity: 0.4;
        }
    `}
  }

  .compressor-header {
    padding-left: ${({ theme: { token } }) => token.paddingXS}px;
  }

  .modulate-header {
    padding-right: ${({ theme: { token } }) => token.padding}px;
  }

  .info-message {
    font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
    color: ${({ theme: { token } }) => token.colorTextDisabled};
  }

`;

interface CompressorNameContainerProps {
  $disabledCompressor?: boolean;
}

export const CompressorNameContainer = styled.div<CompressorNameContainerProps>`
  padding: ${({ theme: { token } }) => `${token.paddingXXS}px ${token.padding}px`};
  margin: 0 ${({ theme: { token } }) => token.marginXS}px 0 0;
  border-radius: 40px;
  color: ${({ theme: { token } }) => token.colorTextTertiary};
  ${({ $disabledCompressor }) => $disabledCompressor && 'opacity: 0.4;'}
  width: fit-content;
`;

interface InputProps {
  $hasBeenEdited: boolean;
  $valueLength?: number;
  $fromRulesEditor?: boolean;
  $hasFinishedWizard?: boolean;
  theme: {
    token: GlobalToken;
  };
}

export const Input = styled(EPInput)<InputProps>`
  width: 60px;

  &.ant-input {
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    color: ${({ theme: { token } }) => token.colorPrimary};
    padding-left: ${({ theme: { token } }) => token.paddingXXS}px;
    padding-right: 0;
    height: 21px;
    ${({ value }) => (value || value === 0) && 'text-decoration: underline;'}
    ${({ $fromRulesEditor, $hasFinishedWizard, theme: { token } }) =>
      ($fromRulesEditor || $hasFinishedWizard) &&
      `
        background-color: ${$fromRulesEditor ? token.colorBgBase : token.colorBgContainer};
        border: none;
      `}
      width: ${({ $valueLength }) => ($valueLength ? `calc(${$valueLength}ch + 1ch);` : '18px;')}

    &::placeholder {
      color: ${({ theme: { token } }) => token.colorPrimary};
    }
  }
`;

interface CheckboxContainerProps {
  $hasBeenEdited: boolean;
  $fromRulesEditor: boolean;
  $disabledCompressor: boolean;
  $hasFinishedWizard?: boolean;
  theme: {
    token: GlobalToken;
  };
}

export const CheckboxContainer = styled.div<CheckboxContainerProps>`
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $hasBeenEdited, $fromRulesEditor, $hasFinishedWizard, theme: { token } }) =>
    $hasBeenEdited && ($fromRulesEditor || $hasFinishedWizard) ? token.colorBorder : 'transparent'};
  border-radius: 2px;
  padding-bottom: ${({ theme: { token } }) => token.paddingXXS}px;
  ${({ $disabledCompressor }: CheckboxContainerProps) => $disabledCompressor && 'opacity: 0.4;'}
`;

